export interface AlbumUploadResumeRecord {
    id: string;
    studioId: string;
    eventId: string;
    serviceId?: string | null;
    templateId: string;
    fileName: string;
    fileSize: number;
    lastModified: number;
    blobName: string;
    blockSize: number;
    totalBlocks: number;
    completedBlockIndexes: number[];
    updatedAt: number;
}

const DB_NAME = "psp-album-upload-resume";
const DB_VERSION = 1;
const STORE_NAME = "uploads";
const DEFAULT_RECORD_TTL_MS = 7 * 24 * 60 * 60 * 1000;

let databasePromise: Promise<IDBDatabase> | null = null;

const isIndexedDbAvailable = () => typeof indexedDB !== "undefined";

const waitForRequest = <T>(request: IDBRequest<T>) => new Promise<T>((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("IndexedDB request failed"));
});

const waitForTransaction = (transaction: IDBTransaction) => new Promise<void>((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error ?? new Error("IndexedDB transaction failed"));
    transaction.onabort = () => reject(transaction.error ?? new Error("IndexedDB transaction aborted"));
});

const openDatabase = async (): Promise<IDBDatabase> => {
    if (!isIndexedDbAvailable()) {
        throw new Error("IndexedDB is not available");
    }
    if (!databasePromise) {
        databasePromise = new Promise<IDBDatabase>((resolve, reject) => {
            const request = indexedDB.open(DB_NAME, DB_VERSION);
            request.onupgradeneeded = () => {
                const database = request.result;
                if (!database.objectStoreNames.contains(STORE_NAME)) {
                    const store = database.createObjectStore(STORE_NAME, { keyPath: "id" });
                    store.createIndex("updatedAt", "updatedAt", { unique: false });
                }
            };
            request.onsuccess = () => resolve(request.result);
            request.onerror = () => reject(request.error ?? new Error("Failed to open IndexedDB"));
        });
    }
    return databasePromise;
};

const withStore = async <T>(
    mode: IDBTransactionMode,
    operation: (store: IDBObjectStore, transaction: IDBTransaction) => Promise<T>,
): Promise<T> => {
    const database = await openDatabase();
    const transaction = database.transaction(STORE_NAME, mode);
    const store = transaction.objectStore(STORE_NAME);
    const result = await operation(store, transaction);
    await waitForTransaction(transaction);
    return result;
};

export const buildAlbumUploadResumeId = ({
    studioId,
    eventId,
    serviceId,
    templateId,
    fileName,
    fileSize,
    lastModified,
}: Pick<AlbumUploadResumeRecord, "studioId" | "eventId" | "serviceId" | "templateId" | "fileName" | "fileSize" | "lastModified">) => (
    [
        studioId,
        eventId,
        serviceId || "",
        templateId,
        (fileName || "").trim().toLowerCase(),
        String(fileSize || 0),
        String(lastModified || 0),
    ].join("::")
);

export const getAlbumUploadResumeRecord = async (id: string): Promise<AlbumUploadResumeRecord | null> => {
    if (!isIndexedDbAvailable()) return null;
    return withStore("readonly", async (store) => {
        const record = await waitForRequest(store.get(id) as IDBRequest<AlbumUploadResumeRecord | undefined>);
        return record ?? null;
    });
};

export const saveAlbumUploadResumeRecord = async (record: AlbumUploadResumeRecord): Promise<void> => {
    if (!isIndexedDbAvailable()) return;
    await withStore("readwrite", async (store) => {
        await waitForRequest(store.put(record));
    });
};

export const deleteAlbumUploadResumeRecord = async (id: string): Promise<void> => {
    if (!isIndexedDbAvailable()) return;
    await withStore("readwrite", async (store) => {
        await waitForRequest(store.delete(id));
    });
};

export const purgeStaleAlbumUploadResumeRecords = async (maxAgeMs = DEFAULT_RECORD_TTL_MS): Promise<void> => {
    if (!isIndexedDbAvailable()) return;
    const cutoff = Date.now() - maxAgeMs;
    await withStore("readwrite", async (store) => {
        const records = await waitForRequest(store.getAll() as IDBRequest<AlbumUploadResumeRecord[]>);
        await Promise.all(
            (records || [])
                .filter((record) => !record?.updatedAt || record.updatedAt < cutoff)
                .map((record) => waitForRequest(store.delete(record.id))),
        );
    });
};
